import type { RawBar, RawPositionInput } from '@trading/positions-ui';
import type { AppConfig } from './config.js';

interface AlpacaPositionResponse {
    symbol: string;
    qty: string;
    side: 'long' | 'short';
    avg_entry_price: string;
    current_price: string;
    market_value: string;
    unrealized_pl: string;
    unrealized_plpc: string;
}

interface AlpacaBarsResponse {
    bars?: RawBar[];
}

const tradingBaseUrl = (cfg: AppConfig): string =>
    cfg.alpacaPaper ? 'https://paper-api.alpaca.markets' : 'https://api.alpaca.markets';

const authHeaders = (cfg: AppConfig): Record<string, string> => ({
    'APCA-API-KEY-ID': cfg.alpacaKeyId,
    'APCA-API-SECRET-KEY': cfg.alpacaSecret,
});

const assertOk = (response: Response): void => {
    if (!response.ok) {
        throw new Error(`Alpaca request failed: ${response.status} ${response.statusText}`);
    }
};

const toRawPosition = (position: AlpacaPositionResponse): RawPositionInput => ({
    symbol: position.symbol,
    side: position.side,
    qty: Number(position.qty),
    avgEntryPrice: Number(position.avg_entry_price),
    currentPrice: Number(position.current_price),
    marketValue: Number(position.market_value),
    unrealizedPl: Number(position.unrealized_pl),
    unrealizedPlPct: Number(position.unrealized_plpc) * 100,
});

export const getPositions = async (cfg: AppConfig): Promise<RawPositionInput[]> => {
    const response = await fetch(`${tradingBaseUrl(cfg)}/v2/positions`, {
        headers: authHeaders(cfg),
    });
    assertOk(response);

    const positions = (await response.json()) as AlpacaPositionResponse[];
    return positions.map(toRawPosition);
};

export const getBars = async (cfg: AppConfig, symbol: string, limit = 30): Promise<RawBar[]> => {
    const url = `https://data.alpaca.markets/v2/stocks/${symbol}/bars?timeframe=1Day&limit=${limit}&feed=iex&adjustment=raw`;
    const response = await fetch(url, { headers: authHeaders(cfg) });
    assertOk(response);

    const body = (await response.json()) as AlpacaBarsResponse;
    return body.bars ?? [];
};

export const closePosition = async (cfg: AppConfig, symbol: string): Promise<void> => {
    const response = await fetch(`${tradingBaseUrl(cfg)}/v2/positions/${symbol}`, {
        method: 'DELETE',
        headers: authHeaders(cfg),
    });
    assertOk(response);
};
